import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Content } from "../../components/wrappers/Content";
import { useFetch } from "../../lib/useFetch";
import { useUserStore } from "../../stores/userStore";
import { cn } from "../../lib/utils";
import { CheckIcon, ClockIcon, LayoutGridIcon, ListIcon } from "lucide-react";

/**
 * Broker queue — the HubSpot-board replacement (Phase C, ws-screens.js). One
 * fetch fills the board; cards arrive sorted by next action (overdue → warm →
 * longest in stage). Drag advances stage optimistically. Board or dense table
 * view; keyboard: N new offer, / search, J/K move, Enter open (table view).
 */

type QueueCard = {
  id: string;
  stage: string;
  title: string;
  clientName: string | null;
  brokerId: string | null;
  brokerName: string | null;
  brand: string | null;
  value: number;
  currency: string | null;
  yachtCount: number;
  leadYacht: string | null;
  updatedAt: string;
  daysInStage: number;
  nextAction: string;
  overdue: boolean;
  slaDays: number;
  opens: number;
  lastClientActivityAt: string | null;
  warm: boolean;
  bookingId: string | null;
  valueGbp: number | null;
};

const STAGES: { key: string; label: string }[] = [
  { key: "enquiry", label: "Enquiry" },
  { key: "options_sent", label: "Options sent" },
  { key: "offer", label: "Offer" },
  { key: "negotiating", label: "Negotiating" },
  { key: "won", label: "Won" },
];

type Filter = "mine" | "all" | "needs_action" | "this_week";
type BrandFilter = "all" | "helm" | "anchor";
type View = "board" | "table";

const fmt = (v: number, cur: string | null) =>
  v ? new Intl.NumberFormat("en-EN", { style: "currency", currency: cur ?? "EUR", maximumFractionDigits: 0 }).format(v) : "—";

const ago = (iso: string | null) => {
  if (!iso) return null;
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function Queue() {
  const [cards, setCards] = useState<QueueCard[]>([]);
  const [filter, setFilter] = useState<Filter>("mine");
  const [brand, setBrand] = useState<BrandFilter>("all");
  const [view, setView] = useState<View>(() => (localStorage.getItem("queue:view") as View) || "board");
  const [search, setSearch] = useState("");
  const [dragId, setDragId] = useState<string | null>(null);
  const [focus, setFocus] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const { fetch } = useFetch();
  const navigate = useNavigate();
  const user = useUserStore().user;
  // Cards carry the DB broker id; the session's `id` is the HubSpot owner id.
  const myId = user?.databaseId != null ? String(user.databaseId) : null;

  const load = useCallback(() => {
    fetch<{ data: QueueCard[] }>("/api/offers/workspace/queue")
      .then((r) => setCards(r.data ?? []))
      .catch(() => setCards([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    localStorage.setItem("queue:view", view);
  }, [view]);

  const openCard = useCallback(
    (c: QueueCard) => navigate(c.stage === "won" && c.bookingId ? `/bookings/${c.bookingId}?tab=overview` : `/offers/${c.id}`),
    [navigate],
  );

  // Scope (mine/all + brand) first — the needs-action badge counts within it.
  const scoped = useMemo(
    () =>
      cards.filter((c) => {
        if (filter === "mine" && myId && c.brokerId !== myId) return false;
        if (brand !== "all" && c.brand !== brand) return false;
        return true;
      }),
    [cards, filter, brand, myId],
  );
  const needsActionCount = useMemo(() => scoped.filter((c) => c.overdue).length, [scoped]);

  const visible = useMemo(() => {
    const weekAgo = Date.now() - 7 * 86400000;
    return scoped.filter((c) => {
      if (filter === "needs_action" && !c.overdue) return false;
      if (filter === "this_week" && new Date(c.updatedAt).getTime() < weekAgo) return false;
      if (search) {
        const s = search.toLowerCase();
        if (
          !(c.clientName ?? "").toLowerCase().includes(s) &&
          !(c.title ?? "").toLowerCase().includes(s) &&
          !(c.leadYacht ?? "").toLowerCase().includes(s)
        )
          return false;
      }
      return true;
    });
  }, [scoped, filter, search]);

  // keyboard: N new offer, / search, J/K + Enter in table view
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "n" || e.key === "N") navigate("/offers/add");
      if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (view === "table") {
        if (e.key === "j" || e.key === "J" || e.key === "ArrowDown") {
          e.preventDefault();
          setFocus((f) => Math.min(f + 1, Math.max(visible.length - 1, 0)));
        }
        if (e.key === "k" || e.key === "K" || e.key === "ArrowUp") {
          e.preventDefault();
          setFocus((f) => Math.max(f - 1, 0));
        }
        if (e.key === "Enter" && visible[focus]) openCard(visible[focus]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, view, visible, focus, openCard]);

  const moveCard = (id: string, stage: string) => {
    const prev = cards;
    setCards((cs) => cs.map((c) => (c.id === id ? { ...c, stage } : c))); // optimistic
    fetch(`/api/offers/workspace/queue/${id}/stage`, "PATCH", { stage }).catch(() => setCards(prev));
  };

  const activeCount = scoped.filter((c) => c.stage !== "won").length;

  return (
    <Content title="My queue">
      <div className="mb-4 flex flex-wrap items-center gap-2 px-1">
        {(
          [
            ["mine", "My deals"],
            ["all", "All brokers"],
            ["needs_action", "Needs action"],
            ["this_week", "This week"],
          ] as [Filter, string][]
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm",
              filter === k ? "border-blue-600 bg-blue-600 text-white" : "bg-white hover:bg-slate-50",
            )}
          >
            {k === "needs_action" && <ClockIcon size={14} />}
            {label}
            {k === "needs_action" && needsActionCount > 0 && (
              <span
                className={cn(
                  "rounded-full px-1.5 text-[11px] font-semibold leading-4",
                  filter === k ? "bg-white/20 text-white" : "bg-red-100 text-red-700",
                )}
              >
                {needsActionCount}
              </span>
            )}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-slate-200" />
        {(
          [
            ["all", "Both"],
            ["helm", "HELM"],
            ["anchor", "Anchor"],
          ] as [BrandFilter, string][]
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setBrand(k)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-sm",
              brand === k ? "border-slate-800 bg-slate-800 text-white" : "bg-white hover:bg-slate-50",
            )}
          >
            {label}
          </button>
        ))}
        <input
          ref={searchRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search client, yacht… ( / )"
          className="ml-auto w-64 rounded-md border border-input bg-white px-3 py-1.5 text-sm"
        />
        <div className="flex rounded-md border bg-white">
          <button
            aria-label="Board view"
            onClick={() => setView("board")}
            className={cn("rounded-l-md p-1.5", view === "board" ? "bg-slate-800 text-white" : "hover:bg-slate-50")}
          >
            <LayoutGridIcon size={16} />
          </button>
          <button
            aria-label="Table view"
            onClick={() => setView("table")}
            className={cn("rounded-r-md p-1.5", view === "table" ? "bg-slate-800 text-white" : "hover:bg-slate-50")}
          >
            <ListIcon size={16} />
          </button>
        </div>
      </div>
      <div className="mb-3 px-1 text-xs text-slate-400">
        {activeCount} active · sorted by next action · <b>N</b> new enquiry · <b>/</b> search ·{" "}
        {view === "board" ? "drag cards to advance stage" : (
          <>
            <b>J</b>/<b>K</b> move · <b>Enter</b> open
          </>
        )}
      </div>

      {view === "board" ? (
        <div className="grid grid-cols-5 items-start gap-3 px-1">
          {STAGES.map((stage) => {
            const col = visible.filter((c) => c.stage === stage.key);
            // Column totals in £ (base reporting currency); card values stay native.
            const total = col.reduce((s, c) => s + (c.valueGbp ?? 0), 0);
            return (
              <div
                key={stage.key}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragId) moveCard(dragId, stage.key);
                  setDragId(null);
                }}
                className="min-h-[240px] rounded-xl border bg-slate-50 p-2"
              >
                <div className="flex items-baseline justify-between px-1.5 py-1">
                  <span className="text-sm font-semibold">{stage.label}</span>
                  <span className="text-xs text-slate-500">
                    {col.length} · {fmt(total, "GBP")}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {col.map((c) => (
                    <QueueCardView
                      key={c.id}
                      card={c}
                      showBroker={filter === "all"}
                      onDragStart={() => setDragId(c.id)}
                      onOpen={() => openCard(c)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Stage</th>
                <th className="px-3 py-2">Yacht</th>
                <th className="px-3 py-2 text-right">Value</th>
                <th className="px-3 py-2">In stage</th>
                <th className="px-3 py-2">Next action</th>
                <th className="px-3 py-2">Client activity</th>
                {filter === "all" && <th className="px-3 py-2">Broker</th>}
              </tr>
            </thead>
            <tbody>
              {visible.map((c, i) => (
                <tr
                  key={c.id}
                  onClick={() => openCard(c)}
                  onMouseEnter={() => setFocus(i)}
                  className={cn(
                    "cursor-pointer border-t hover:bg-slate-50",
                    i === focus && "bg-blue-50",
                    c.overdue && "border-l-2 border-l-red-500",
                    !c.overdue && c.warm && "border-l-2 border-l-amber-400",
                  )}
                >
                  <td className="px-3 py-2 font-medium">{c.clientName ?? c.title}</td>
                  <td className="px-3 py-2 text-slate-600">{STAGES.find((s) => s.key === c.stage)?.label ?? c.stage}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {c.leadYacht ?? "—"}
                    {c.yachtCount > 1 ? ` +${c.yachtCount - 1}` : ""}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(c.value, c.currency)}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{c.stage === "won" ? "—" : `${c.daysInStage}d`}</td>
                  <td className={cn("px-3 py-2", c.overdue ? "font-medium text-red-600" : c.warm ? "text-amber-700" : "text-slate-700")}>
                    {c.nextAction}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {c.opens ? `opened ${c.opens}×` : ""}
                    {c.opens && c.lastClientActivityAt ? " · " : ""}
                    {ago(c.lastClientActivityAt) ?? (c.opens ? "" : "—")}
                  </td>
                  {filter === "all" && <td className="px-3 py-2 text-slate-500">{c.brokerName ?? "—"}</td>}
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-slate-400">
                    Nothing here — try another filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Content>
  );
}

function QueueCardView({
  card: c,
  showBroker,
  onDragStart,
  onOpen,
}: {
  card: QueueCard;
  showBroker: boolean;
  onDragStart: () => void;
  onOpen: () => void;
}) {
  const done = c.stage === "won";
  const activity = ago(c.lastClientActivityAt);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onOpen}
      className={cn(
        "cursor-pointer rounded-lg border bg-white p-2.5 hover:shadow-sm",
        c.overdue && !done && "border-l-[3px] border-l-red-500",
        c.warm && !c.overdue && !done && "border-l-[3px] border-l-amber-400",
        done && "border-l-[3px] border-l-green-500",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="line-clamp-1 text-sm font-medium leading-tight">{c.clientName ?? c.title}</span>
        <span className="whitespace-nowrap text-xs text-slate-500">{c.value ? fmt(c.value, c.currency) : "new"}</span>
      </div>
      <div className="mt-0.5 line-clamp-1 text-xs text-slate-500">
        {c.leadYacht ?? "No yacht yet"}
        {c.yachtCount > 1 ? ` +${c.yachtCount - 1}` : ""}
      </div>
      {(c.opens > 0 || activity) && !done && (
        <div className="mt-1 text-[11px] text-amber-700">
          {c.opens ? `opened ${c.opens}×` : ""}
          {c.opens && activity ? " · " : ""}
          {activity ? `active ${activity}` : ""}
        </div>
      )}
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span
          className={cn(
            "flex items-center gap-1 text-xs font-medium",
            done ? "text-green-700" : c.overdue ? "text-red-600" : c.warm ? "text-amber-700" : "text-slate-500",
          )}
        >
          {done ? <CheckIcon size={12} /> : <ClockIcon size={12} />}
          {c.nextAction}
        </span>
        <span className="text-[11px] text-slate-400">{done ? "confirmed" : `${c.daysInStage}d in stage`}</span>
      </div>
      {showBroker && c.brokerName && <div className="mt-1 text-[11px] text-slate-400">{c.brokerName}</div>}
    </div>
  );
}
